import { useCallback, useMemo, useRef, useState } from 'react';

/**
 * Hook to manage dropdown subpanel state and toggle logic.
 * Automatically handles opening/closing and provides a click handler for the trigger element.
 * 
 * HOVER BEHAVIOR: To enable hover-to-open behavior, add `onMouseEnter` and `onMouseLeave`
 * handlers to both the trigger element and the `DropdownSubPanel` component. Use a ref to
 * store a timeout for delayed closing:
 * 
 * ```typescript
 * const panel = useDropdownSubPanel();
 * const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null);
 * 
 * // On trigger element:
 * <div
 *   ref={panel.triggerRef}
 *   onMouseEnter={() => {
 *     if (hoverTimeoutRef.current) {
 *       clearTimeout(hoverTimeoutRef.current);
 *       hoverTimeoutRef.current = null;
 *     }
 *     panel.open();
 *   }}
 *   onMouseLeave={() => {
 *     hoverTimeoutRef.current = setTimeout(() => {
 *       panel.close();
 *       hoverTimeoutRef.current = null;
 *     }, 150);
 *   }}
 * >
 * 
 * // On DropdownSubPanel:
 * <DropdownSubPanel
 *   onMouseEnter={() => {
 *     if (hoverTimeoutRef.current) {
 *       clearTimeout(hoverTimeoutRef.current);
 *       hoverTimeoutRef.current = null;
 *     }
 *   }}
 *   onMouseLeave={() => {
 *     hoverTimeoutRef.current = setTimeout(() => {
 *       panel.close();
 *       hoverTimeoutRef.current = null;
 *     }, 150);
 *   }}
 * />
 * 
 * // Cleanup:
 * useEffect(() => {
 *   return () => {
 *     if (hoverTimeoutRef.current) {
 *       clearTimeout(hoverTimeoutRef.current);
 *     }
 *   };
 * }, []);
 * ```
 * 
 * See `RecordingActions.tsx` or `WorkActions.tsx` for complete working examples.
 */
export const useDropdownSubPanel = () => {
  const [isOpen, setIsOpen] = useState(false);
  const triggerRef = useRef<HTMLElement | null>(null);

  const open = useCallback(() => {
    setIsOpen(true);
  }, []);

  const close = useCallback(() => {
    setIsOpen(false);
  }, []);

  const toggle = useCallback(() => {
    setIsOpen((prev) => !prev);
  }, []);

  const handleTriggerClick = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      e.nativeEvent.stopImmediatePropagation();
      toggle();
    },
    [toggle]
  );

  return useMemo(
    () => ({
      isOpen,
      setIsOpen,
      open,
      close,
      toggle,
      triggerRef,
      handleTriggerClick,
    }),
    [close, handleTriggerClick, isOpen, open, setIsOpen, toggle, triggerRef]
  );
};
